import React, { useState } from "react";
import {
  IconButton,
  Badge,
  MenuItem,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import MailIcon from "@mui/icons-material/Mail";
import { useNavigate } from "react-router-dom";
import BellDropdownMenu from "../common/BellDropdownMenu";

function MessageBell({ messages, onMarkAsRead, iconColor, navTone = "dark" }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isLightNav = navTone === "light";
  const resolvedIconColor = iconColor ?? (isLightNav ? "#334155" : "#fff");
  const navigate = useNavigate();

  const unreadCount = messages.filter((msg) => !msg.isRead).length;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const formatSender = (from) => {
    if (!from) return { name: "Unknown", email: "" };

    const matches = from.match(/"?([^"<]+)"?\s*<?([^>]*)>?/);

    if (matches && matches.length >= 3) {
      return {
        name: matches[1].trim(),
        email: matches[2].trim(),
      };
    }

    return { name: from, email: "" };
  };

  const sortedMessages = [...messages].sort((a, b) => {
    if (!a.isRead && b.isRead) return -1;
    if (a.isRead && !b.isRead) return 1;
    return new Date(b.date) - new Date(a.date);
  });

  const handleMessageClick = (message) => {
    if (!message.isRead && onMarkAsRead) {
      onMarkAsRead(message.uid);
    }
    handleClose();
    navigate("/messages", { state: { selectedMessage: message } });
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: resolvedIconColor,
          position: "relative",
          ...(isLightNav && {
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.04)" },
          }),
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <MailIcon />
        </Badge>
      </IconButton>

      <BellDropdownMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        titleLabel="Messages"
        unreadCount={unreadCount}
        showFooter={sortedMessages.length > 0}
        footerTo="/messages"
        footerLabel="View All Messages"
      >
        {sortedMessages.length === 0 ? (
          <Box sx={{ px: 2, py: 2, display: "flex", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              No new messages
            </Typography>
          </Box>
        ) : (
          sortedMessages.map((msg) => {
            const isUnread = !msg.isRead;
            const fromColor = isUnread ? "#FF8C00" : "#12014B";
            const subjectColor = isUnread ? "#8D00F2" : "#4B4B4B";
            const sender = formatSender(msg.from);

            return (
              <MenuItem
                key={msg.uid}
                onClick={() => handleMessageClick(msg)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid rgba(0,0,0,0.04)",
                  backgroundColor: isUnread
                    ? "rgba(255, 140, 0, 0.04)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isUnread
                      ? "rgba(255, 140, 0, 0.08)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    src={msg.avatar || "/images/user.png"}
                    sx={{ width: 40, height: 40, mr: 1 }}
                  >
                    {sender.name ? sender.name.charAt(0) : ""}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: fromColor, fontWeight: 600 }}
                    >
                      {sender.name}
                    </Typography>
                    {sender.email && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          display: "block",
                          fontSize: "0.75rem",
                        }}
                      >
                        {sender.email}
                      </Typography>
                    )}
                  </Box>
                  {isUnread && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#FF8C00",
                        ml: 1,
                      }}
                    />
                  )}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: subjectColor,
                    fontWeight: 600,
                    mt: 0.5,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    maxWidth: "280px",
                  }}
                >
                  {msg.subject || "No subject"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#4A4A4A",
                    mt: 0.5,
                    fontSize: "0.85rem",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    maxWidth: "280px",
                  }}
                >
                  {msg.text || "No content available"}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: "#8F8F8F", mt: 0.5, alignSelf: "flex-end" }}
                >
                  {new Date(msg.date).toLocaleString()}
                </Typography>
              </MenuItem>
            );
          })
        )}
      </BellDropdownMenu>
    </>
  );
}

export default MessageBell;
